<template>
  <div class="custom-date-picker">
    <input
      :value="displayValue"
      type="text"
      :placeholder="placeholder"
      class="input-text"
      @focus="handleFocus"
      @click="handleClick"
      @input="handleInput"
      :readonly="readonly"
      :required="required"
      :disabled="readonly"
    />
    <div class="calendar-icon" :class="{ 'calendar-icon-disabled': readonly }" @click="handleClick">
      <img src="@/assets/calendar-icon.svg" alt="calendar" />
    </div>
    <div class="separator"></div>

    <!-- Month/Year Picker Modal -->
    <Teleport :to="teleportTarget">
      <div
        v-if="showPicker && pickerType === 'month-year'"
        class="month-year-picker-overlay"
        @click="closePicker"
      >
        <div class="month-year-picker" @click.stop>
          <div class="picker-header">
            <h3>Seleccionar Mes y Año</h3>
            <button class="close-button" @click="closePicker">×</button>
          </div>

          <div class="year-selector">
            <button class="year-nav" @click="decrementYear">‹</button>
            <span class="current-year">{{ selectedYear }}</span>
            <button class="year-nav" @click="incrementYear">›</button>
          </div>

          <div class="months-grid">
            <button
              v-for="(month, index) in months"
              :key="index"
              class="month-button"
              :class="{
                selected: selectedMonth === index,
                disabled: isMonthDisabled(index),
              }"
              :disabled="isMonthDisabled(index)"
              @click="!isMonthDisabled(index) && selectMonth(index)"
            >
              {{ month }}
            </button>
          </div>

          <div class="picker-actions">
            <CustomButton text="Cancelar" variant="cancel" @click="closePicker" />
            <CustomButton text="Confirmar" variant="secondary" @click="confirmMonthYear" />
          </div>
        </div>
      </div>
    </Teleport>
    <span v-if="required && !modelValue" class="required-text">
      {{ errorMessage }}
    </span>
    <!-- Full Date Picker Modal -->
    <Teleport :to="teleportTarget">
      <div
        v-if="showPicker && pickerType === 'date'"
        class="date-picker-overlay"
        @click="closePicker"
      >
        <div class="date-picker" @click.stop>
          <div class="picker-header">
            <h3>Seleccionar Fecha</h3>
            <button class="close-button" @click="closePicker">×</button>
          </div>

          <div class="year-selector">
            <button class="year-nav" @click="decrementDateYear">‹</button>
            <span class="current-year">{{ selectedDateYear }}</span>
            <button class="year-nav" @click="incrementDateYear">›</button>
          </div>

          <div class="month-selector">
            <button class="month-nav" @click="decrementDateMonth">‹</button>
            <span class="current-month">{{ months[selectedDateMonth] }}</span>
            <button class="month-nav" @click="incrementDateMonth">›</button>
          </div>

          <div class="days-grid">
            <div class="day-header" v-for="day in dayHeaders" :key="day">{{ day }}</div>
            <button
              v-for="day in calendarDays"
              :key="day.value"
              class="day-button"
              :class="{
                selected: day.value === selectedDateDay && day.isCurrentMonth,
                'other-month': !day.isCurrentMonth,
                today: day.isToday,
              }"
              @click="day.value && selectDateDay(Number(day.value))"
              :disabled="!day.isCurrentMonth"
            >
              {{ day.value }}
            </button>
          </div>

          <div class="picker-actions">
            <CustomButton text="Cancelar" variant="cancel" @click="closePicker" />
            <CustomButton text="Confirmar" variant="secondary" @click="confirmDate" />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { months, dayHeaders } from '@/utils/dateParsing'
import CustomButton from './CustomButton.vue'

interface Props {
  modelValue: string
  pickerType: 'month-year' | 'date'
  placeholder?: string
  hasError?: boolean
  readonly?: boolean
  required?: boolean
  allowFutureMonths?: boolean
  teleport?: string
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  hasError: false,
  readonly: false,
  required: false,
  allowFutureMonths: true,
  teleport: 'body',
  errorMessage: 'Es necesario seleccionar una fecha para continuar',
})

// Computed property to ensure teleport target exists
const teleportTarget = ref('body')

onMounted(() => {
  if (props.teleport === 'body') {
    teleportTarget.value = 'body'
  } else {
    // Check if the target element exists
    const target = document.querySelector(props.teleport)
    if (target) {
      teleportTarget.value = props.teleport
    } else {
      // Fallback to body if target doesn't exist
      console.warn(`Teleport target "${props.teleport}" not found, falling back to body`)
      teleportTarget.value = 'body'
    }
  }
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  focus: []
}>()

// Month/Year picker state
const showPicker = ref(false)
const selectedYear = ref(new Date().getFullYear())
const selectedMonth = ref(new Date().getMonth())

// Date picker state
const selectedDateYear = ref(new Date().getFullYear())
const selectedDateMonth = ref(new Date().getMonth())
const selectedDateDay = ref(new Date().getDate())

// Computed properties
const displayValue = computed(() => props.modelValue)

// Check if a month should be disabled: when `allowFutureMonths` is false,
// only the 12-month window starting at the current month is enabled.
// That is, candidate months with a difference in months from now
// outside the range [0, 11] are disabled.
const isMonthDisabled = (monthIndex: number) => {
  if (props.allowFutureMonths) return false

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth()

  // Calculate months as totals to compare (year * 12 + month)
  const candidateTotal = selectedYear.value * 12 + monthIndex
  const currentTotal = currentYear * 12 + currentMonth
  const diff = candidateTotal - currentTotal

  // Allow all past months. Disable only if it's further than 12 months ahead (diff > 12).
  return diff > 12
}

const calendarDays = computed(() => {
  const year = selectedDateYear.value
  const month = selectedDateMonth.value
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const daysInMonth = lastDay.getDate()
  const startDay = firstDay.getDay()

  const days = []

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startDay; i++) {
    days.push({ value: '', isCurrentMonth: false, isToday: false })
  }

  // Add days of the current month
  const today = new Date()
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday =
      year === today.getFullYear() && month === today.getMonth() && day === today.getDate()
    days.push({ value: day, isCurrentMonth: true, isToday })
  }

  return days
})

// Methods
const closePicker = () => {
  showPicker.value = false
}

// Handle focus event
const handleFocus = () => {
  if (!props.readonly) {
    emit('focus')
  }
}

// Handle click event - only open picker if not readonly
const handleClick = () => {
  if (!props.readonly) {
    showPicker.value = true
  }
}

// Month/Year picker methods
const incrementYear = () => {
  selectedYear.value++
}

const decrementYear = () => {
  selectedYear.value--
}

const selectMonth = (monthIndex: number) => {
  selectedMonth.value = monthIndex
}

const confirmMonthYear = () => {
  const monthName = months[selectedMonth.value]
  const value = `${monthName} de ${selectedYear.value}`
  emit('update:modelValue', value)
  closePicker()
}

// Date picker methods
const incrementDateYear = () => {
  selectedDateYear.value++
}

const decrementDateYear = () => {
  selectedDateYear.value--
}

const incrementDateMonth = () => {
  if (selectedDateMonth.value === 11) {
    selectedDateMonth.value = 0
    selectedDateYear.value++
  } else {
    selectedDateMonth.value++
  }
}

const decrementDateMonth = () => {
  if (selectedDateMonth.value === 0) {
    selectedDateMonth.value = 11
    selectedDateYear.value--
  } else {
    selectedDateMonth.value--
  }
}

const selectDateDay = (day: number) => {
  selectedDateDay.value = day
}

const confirmDate = () => {
  const day = selectedDateDay.value.toString().padStart(2, '0')
  const month = (selectedDateMonth.value + 1).toString().padStart(2, '0')
  const year = selectedDateYear.value
  const value = `${day}/${month}/${year}`
  emit('update:modelValue', value)
  closePicker()
}

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value

  if (props.pickerType === 'date') {
    // Allow manual input but validate format
    if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = value.split('/').map(Number)
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        selectedDateDay.value = day
        selectedDateMonth.value = month - 1
        selectedDateYear.value = year
      }
    }
  }

  emit('update:modelValue', value)
}

// Watch for external value changes to update internal state
watch(
  () => props.modelValue,
  (newValue) => {
    if (props.pickerType === 'month-year' && newValue) {
      // Parse "Mayo de 2025" format
      const match = newValue.match(/(\w+) de (\d{4})/)
      if (match) {
        const monthName = match[1]
        const year = parseInt(match[2])
        const monthIndex = months.findIndex((m) => m === monthName)
        if (monthIndex !== -1) {
          selectedMonth.value = monthIndex
          selectedYear.value = year
        }
      }
    } else if (props.pickerType === 'date' && newValue) {
      // Parse "28/09/2025" format
      const match = newValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (match) {
        const day = parseInt(match[1])
        const month = parseInt(match[2])
        const year = parseInt(match[3])
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
          selectedDateDay.value = day
          selectedDateMonth.value = month - 1
          selectedDateYear.value = year
        }
      }
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.custom-date-picker {
  position: relative;
  width: 100%;
}

.input-text {
  width: 100%;
  height: 48px;
  background: #f2f2f2;
  border-radius: 16px;
  border: none;
  padding: 0 40px 0 16px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  outline: none;
}

.input-text:disabled {
  background: #e8e8e8;
  color: #a1a1a1;
  cursor: not-allowed;
  opacity: 0.6;
}

.calendar-icon {
  position: absolute;
  width: 24px;
  height: 24px;
  right: 8px;
  top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.calendar-icon-disabled {
  cursor: not-allowed;
  opacity: 0.5;
  pointer-events: none;
}

.calendar-icon img {
  width: 18px;
  height: 18px;
}

.separator {
  position: absolute;
  width: 2px;
  height: 24px;
  right: 40px;
  top: 12px;
  background: #a1a1a1;
}

/* Month/Year Picker Styles */
.month-year-picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.month-year-picker {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.15);
}

.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.picker-header h3 {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 140%;
  color: #2b2a2a;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b6b6b;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: #f5f5f5;
}

.year-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
}

.year-nav {
  background: #f2f2f2;
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #2b2a2a;
  transition: background-color 0.2s;
}

.year-nav:hover {
  background: #e0e0e0;
}

.current-year {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  color: #2b2a2a;
  min-width: 80px;
  text-align: center;
}

.months-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 24px;
}

.month-button {
  background: #f2f2f2;
  border: none;
  border-radius: 8px;
  padding: 12px 8px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  color: #2b2a2a;
  cursor: pointer;
  transition: all 0.2s;
}

.month-button:hover {
  background: #e0e0e0;
}

.month-button.selected {
  background: #003896;
  color: #ffffff;
}

.month-button.disabled {
  background: #f5f5f5;
  color: #a1a1a1;
  cursor: not-allowed;
  opacity: 0.5;
}

.month-button.disabled:hover {
  background: #f5f5f5;
  color: #a1a1a1;
}

.picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-button,
.confirm-button {
  padding: 8px 16px;
  border-radius: 8px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button {
  background: #f2f2f2;
  border: none;
  color: #2b2a2a;
}

.cancel-button:hover {
  background: #e0e0e0;
}

.confirm-button {
  background: #003896;
  border: none;
  color: #ffffff;
}

.confirm-button:hover {
  background: #002a6b;
}

/* Date Picker Styles */
.date-picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.date-picker {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  width: 350px;
  max-width: 90vw;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.15);
}

.month-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;
}

.month-nav {
  background: #f2f2f2;
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #2b2a2a;
  transition: background-color 0.2s;
}

.month-nav:hover {
  background: #e0e0e0;
}

.current-month {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  min-width: 100px;
  text-align: center;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 20px;
}

.day-header {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 140%;
  color: #6b6b6b;
  text-align: center;
  padding: 8px 4px;
}

.day-button {
  background: #f2f2f2;
  border: none;
  border-radius: 6px;
  padding: 8px 4px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  color: #2b2a2a;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day-button:hover:not(:disabled) {
  background: #e0e0e0;
}

.day-button.selected {
  background: #003896;
  color: #ffffff;
}

.day-button.today {
  background: #e9f0f6;
  color: #003896;
  font-weight: 600;
}

.day-button.today.selected {
  background: #003896;
  color: #ffffff;
}

.day-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.day-button.other-month {
  opacity: 0.3;
  cursor: not-allowed;
}

.required-text {
  width: 100%;
  height: 17px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 130%;
  color: #ff0000;
  margin-top: 4px;
}
</style>
